import { Plugin, PluginKey } from 'prosemirror-state';
import ReactDOM from 'react-dom';
import uuid from 'hubspot-prosemirror/prosemirror/utils/uuid';
import ContentAssistantHighlightButton from './CAHighlightButton';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { jsx as _jsx } from "react/jsx-runtime";
const hasNonTextNode = (state, from, to) => {
  let nonTextNodeFound = false;
  const nativeElements = ['paragraph', 'heading', 'list_item', 'ordered_list', 'text', 'hard_break', 'bullet_list'];
  state.doc.nodesBetween(from.pos, to.pos, node => {
    if (!nativeElements.includes(node.type.name)) {
      nonTextNodeFound = true;
    }
  });
  return nonTextNodeFound;
};
export const getContentAssistantHighlightPlugin = ({
  portalApi,
  selectionCommands,
  trackInteraction,
  usageDescription
}) => {
  const uniqueKey = uuid();
  const pluginKey = new PluginKey(`highlightPlugin_${uniqueKey}`);
  let widgetContainer = undefined;
  let currentPortal = null;
  let mouseupEventHandler;
  const initialState = {
    widgetOpened: false,
    from: 0,
    to: 0
  };
  const closeWidget = () => {
    if (currentPortal) {
      portalApi.destroyPortal(currentPortal.key);
      currentPortal = null;
    }
  };
  const renderWidget = view => {
    if (widgetContainer && currentPortal) {
      return widgetContainer;
    }
    widgetContainer = document.createElement('span');
    widgetContainer.style.position = 'absolute';
    widgetContainer.style.marginLeft = '5px';

    // in mouseup and keyup eventHandler, it will be set to inline-block
    // this is to prevent flickering
    widgetContainer.style.display = 'none';
    const onOpenDropdown = () => {
      view.dispatch(view.state.tr.setMeta(pluginKey, {
        widgetOpened: true,
        from: view.state.selection.from,
        to: view.state.selection.to
      }));
    };
    currentPortal = /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsx(ContentAssistantHighlightButton, {
      view: view,
      onOpenDropdown: onOpenDropdown,
      pluginKey: pluginKey,
      usageDescription: usageDescription,
      selectionCommands: selectionCommands,
      trackInteraction: trackInteraction
    }), widgetContainer, uniqueKey);
    portalApi.createPortal(currentPortal);
    return widgetContainer;
  };
  const rewritePlugin = new Plugin({
    key: pluginKey,
    state: {
      init() {
        mouseupEventHandler = () => {
          if (widgetContainer) widgetContainer.style.display = 'inline-block';
        };
        window.addEventListener('mouseup', mouseupEventHandler);
        return initialState;
      },
      apply(tr, prevState) {
        const meta = tr.getMeta(pluginKey);
        if (meta) return meta;
        if (prevState.widgetOpened) {
          if (tr.selection.$from.pos !== prevState.from || tr.selection.$to.pos !== prevState.to) {
            return initialState;
          }
        }
        return prevState;
      }
    },
    props: {
      decorations: editorState => {
        const from = editorState.selection.$from;
        const to = editorState.selection.$to;
        if (hasNonTextNode(editorState, from, to) || from.pos === to.pos || !to.nodeBefore) {
          closeWidget();
          return null;
        }
        const decorations = [];
        const selection = editorState.selection;
        for (const range of selection.ranges) {
          decorations.push(Decoration.inline(range.$from.pos, range.$to.pos, {
            class: 'highlighted-text'
          }));
        }

        // This is to handle command+A/ctrl+A. When select all is used, the "to" position
        // goes beyond normal range ( === content size), which requires special handling.
        let toPos = to.pos;
        if (to.pos === editorState.doc.content.size) {
          toPos--;
        }
        decorations.push(Decoration.widget(toPos, view => renderWidget(view)));
        return DecorationSet.create(editorState.doc, decorations);
      },
      handleDOMEvents: {
        keyup: () => {
          if (widgetContainer) widgetContainer.style.display = 'inline-block';
        }
      },
      handleClick(view, pos) {
        const {
          to
        } = view.state.selection;
        const {
          size
        } = view.state.doc.content;

        // This is to handle command+A/ctrl+A. When select all is used, the "to" position
        // goes beyond normal range ( === content size), which requires special handling.
        if (to === size && pos === to - 1) {
          return true;
        }
        return false;
      }
    },
    view() {
      return {
        destroy: () => {
          closeWidget();
          window.removeEventListener('mouseup', mouseupEventHandler);
          if (widgetContainer) {
            widgetContainer.remove();
          }
        }
      };
    }
  });
  return rewritePlugin;
};